/**
 * The planet view the world draws from the game's state: tile kinds and heights, the layers under
 * every tile (for the walls of pits) and the blocks laid on them, the rock charges broke, plus what
 * the props need beyond them: the machines, how full each one is over time (a charge: how far its
 * fuse has burnt; an infuser: its hopper), whether it works, and the ground the extractors draw
 * from.
 */
import {
  coreDepth,
  drawCounts,
  extractorGround,
  extractorRate,
  isCharge,
  isInfuser,
  layerAt,
  MACHINES,
  RULES,
  type CrewState,
  type Grid,
  type Machine,
} from '@bramvasterd/terrella-rules';

import { glowOf } from '../models/machines';
import type { MachineView, PlanetView } from './view';

/**
 * How full an extractor is at a time, 0 to 1, from where its clock stood in this state: the rules'
 * accrual (whole periods, up to its storage), with the period under way added in, so a core rises
 * smoothly. Its rate holds until the next state: anything that changes it changes the state.
 */
function fillOf(stored: number, since: number, rate: number, cap: number) {
  return (time: number): number => {
    if (stored >= cap) return 1;
    if (rate === 0 || time <= since) return stored / cap;
    const elapsed = (time - since) / RULES.extractMs;
    const periods = Math.floor(elapsed);
    const whole = stored + periods * rate;
    if (whole >= cap) return 1;
    return Math.min(1, (whole + (elapsed - periods) * rate) / cap);
  };
}

/** A machine as the world draws it: a charge's heart rises as its fuse burns, its splinters turning. */
function machineView(
  view: CrewState,
  tile: number,
  machine: Machine,
  counts: Map<number, number>,
): MachineView | null {
  if (isInfuser(machine)) {
    const { hopper } = MACHINES[machine.type];
    const fill = hopper > 0 ? Math.min(1, machine.hopper.length / hopper) : 0;
    return {
      type: machine.type,
      level: machine.level,
      fillAt: () => fill,
      drawing: machine.lay !== null,
    };
  }
  if (isCharge(machine)) {
    const { fuseMs } = MACHINES[machine.type];
    const lit = machine.done - fuseMs;
    return {
      type: machine.type,
      level: machine.level,
      fillAt: (time) => (fuseMs > 0 ? Math.min(1, Math.max(0, (time - lit) / fuseMs)) : 1),
      drawing: true,
    };
  }
  const level = MACHINES[machine.type].levels[machine.level - 1];
  if (!level) return null;
  const rate = extractorRate(view, tile, counts);
  return {
    type: machine.type,
    level: machine.level,
    fillAt: fillOf(machine.stored, machine.since, rate, level.storage * RULES.parts),
    drawing: rate > 0,
  };
}

export function planetViewOf(view: CrewState, groups: Uint8Array, grid: Grid): PlanetView {
  const counts = drawCounts(view);
  // the ground each extractor draws from glows in its type's colour (one type per ground)
  const drawn = new Uint8Array(grid.count);
  for (const [tile, machine] of view.machines)
    for (const t of extractorGround(view, tile)) drawn[t] = glowOf(machine.type);
  const machines = new Map<number, MachineView>();
  for (const [tile, machine] of view.machines) {
    const shown = machineView(view, tile, machine, counts);
    if (shown) machines.set(tile, shown);
  }
  return {
    grid,
    kind: view.kind,
    elev: view.elev,
    groups,
    layers: {
      coreDepth: coreDepth(view),
      // above the surface only laid blocks lie (the rules throw elsewhere): the tile itself there
      at: (tile, depth) =>
        depth < 0 && !view.laid.has(tile) ? (view.kind[tile] ?? 0) : layerAt(view, tile, depth),
    },
    shattered: view.shattered,
    machines,
    drawn,
  };
}
